import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, API_BASE } from "@/lib/api";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import PageGuide from "@/components/PageGuide";
import SeoMeta from "@/components/SeoMeta";
import ScienceNav from "./ScienceNav";
import {
  ArrowRight, Atom, BookOpen, Globe, HandHeart, Leaf, MagnifyingGlass, Path, Users,
} from "@phosphor-icons/react";
import GuidePdfDownload from "@/components/GuidePdfDownload";
import RelatedReading from "@/components/RelatedReading";

function abs(u) { return !u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`; }

const INTRO = `Science Pulse is NaturePulse's learning platform — connecting science to our world, our communities and our future. Explore topics, ask questions, find answers, and see how science shapes a healthier, more sustainable and innovative planet.`;

/* Mockup display order + friendly labels for the 12 canonical pillars. */
const PILLAR_DISPLAY = [
  { slug: "space-astronomy", label: "Astronomy & Space" },
  { slug: "earth-science", label: "Earth Science" },
  { slug: "oceans-water", label: "Oceans" },
  { slug: "nature-ecology", label: "Nature & Ecology" },
  { slug: "biology", label: "Biology" },
  { slug: "chemistry", label: "Chemistry" },
  { slug: "physics", label: "Physics" },
  { slug: "climate-atmosphere", label: "Climate & Atmosphere" },
  { slug: "human-health", label: "Human & Health Science" },
  { slug: "technology-engineering", label: "Technology & Engineering" },
  { slug: "mathematics", label: "Mathematics" },
  { slug: "science-skills", label: "Science Skills" },
];

const POPULAR_SEARCHES = ["Climate Change", "Space", "Human Body", "Renewable Energy", "AI in Science", "Oceans"];

const VALUE_PROPS = [
  { icon: Globe, title: "1 Planet", sub: "Countless Discoveries" },
  { icon: BookOpen, title: "Unlimited", sub: "Learning" },
  { icon: Users, title: "A Global", sub: "Community" },
  { icon: Leaf, title: "A Brighter", sub: "Tomorrow" },
];

export default function ScienceHome() {
  const [taxonomy, setTaxonomy] = useState(null);
  const [paths, setPaths] = useState([]);
  const [today, setToday] = useState([]);
  const [art, setArt] = useState({});
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    http.get("/science/taxonomy").then(({ data }) => setTaxonomy(data)).catch(() => {});
    http.get("/science/paths").then(({ data }) => setPaths(data.paths || [])).catch(() => {});
    http.get("/science/today", { params: { limit: 3 } }).then(({ data }) => setToday(data.news || [])).catch(() => {});
    http.get("/docket-art/sciencepulse").then(({ data }) => setArt(data.art || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(() => {
      http.get("/science/concepts", { params: { q: q.trim(), limit: 12 } })
        .then(({ data }) => setResults(data.concepts || []))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const pillars = useMemo(() => taxonomy?.pillars || [], [taxonomy]);
  const pillarBySlug = useMemo(() => Object.fromEntries(pillars.map((p) => [p.slug, p])), [pillars]);

  const submitSearch = () => {
    if (results?.length) navigate(`/science/concept/${results[0].concept_id}`);
    else if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <main className="min-h-screen bg-white text-[#0F2A43]" data-testid="science-home">
      <SeoMeta
        title="Science Pulse — Discover the Wonders of Science"
        description="Science Pulse is NaturePulse's learning platform: 12 pillars of science explained at four levels, connected into one knowledge map — from curious kids to university students and lifelong learners."
        canonical={`${origin}/science`}
        jsonLd={{ "@context": "https://schema.org", "@type": "CollectionPage", name: "Science Pulse", url: `${origin}/science` }}
      />

      {/* ---------------- HERO ---------------- */}
      <section className="relative overflow-hidden pt-32 md:pt-36" data-testid="science-hero">
        <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-[#EFF6FF] to-white" />
        <div className="relative max-w-[1200px] mx-auto px-5 md:px-10 pb-12">
          <div className="grid gap-10 lg:grid-cols-[1.15fr_1fr] items-center">
            <div>
              <div className="font-mono-tech text-[11px] tracking-[0.3em] uppercase text-[#2563EB]" data-testid="science-eyebrow">
                Explore · Learn · Discover · Connect
              </div>
              <div className="mt-3 flex items-center gap-3">
                <Atom size={54} weight="duotone" className="text-[#2563EB] shrink-0" />
                <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.98] tracking-tight" data-testid="science-title">
                  <span className="text-[#1E3A8A]">Science</span><span className="text-[#16A34A]">Pulse</span>
                  <sup className="text-lg text-[#1E3A8A]/60">™</sup>
                </h1>
              </div>
              <p className="mt-2 font-serif-display italic text-xl md:text-2xl text-[#1E3A8A]" data-testid="science-tagline">
                Discover the Wonders of Science — For a Brighter Tomorrow
              </p>
              <p className="mt-4 max-w-xl text-base text-[#374151] leading-relaxed" data-testid="science-intro">{INTRO}</p>
              <ListenTranslateBar text={INTRO} testidPrefix="science-home-tools" />
              <div className="mt-6"><PageGuide pageKey="science:home" testidPrefix="science-home" /></div>

              {/* Search */}
              <div className="mt-6 relative max-w-xl" data-testid="science-search-box">
                <div className="flex items-center rounded-full border border-black/10 bg-white shadow-md overflow-hidden focus-within:border-[#2563EB]">
                  <MagnifyingGlass size={16} className="ml-4 text-[#6B7280] shrink-0" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submitSearch()}
                    placeholder="Search science topics, articles, people and more…"
                    data-testid="science-search"
                    className="w-full px-3 py-3.5 text-sm focus:outline-none"
                  />
                  <button onClick={submitSearch} data-testid="science-search-btn"
                    className="m-1 shrink-0 rounded-full bg-[#2563EB] px-6 py-2.5 text-sm font-medium text-white hover:bg-[#1D4ED8] transition-colors">
                    Search
                  </button>
                </div>
                {results && (
                  <div className="absolute z-20 mt-1 w-full rounded-2xl border border-black/10 bg-white shadow-xl overflow-hidden" data-testid="science-search-results">
                    {results.length === 0 && <div className="px-4 py-3 text-sm text-[#6B7280]">Nothing published yet — the map is growing daily.</div>}
                    {results.map((c) => (
                      <Link key={c.concept_id} to={`/science/concept/${c.concept_id}`} data-testid={`science-search-${c.concept_id}`}
                        className="block px-4 py-2.5 hover:bg-[#EFF6FF] border-b border-black/5 last:border-0">
                        <div className="text-sm font-medium text-[#0F2A43]">{c.title}</div>
                        <div className="text-xs text-[#6B7280] line-clamp-1">{c.short_definition}</div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="science-popular-searches">
                <span className="text-xs font-medium text-[#6B7280]">Popular Searches:</span>
                {POPULAR_SEARCHES.map((s) => (
                  <button key={s} onClick={() => setQ(s)} data-testid={`science-popular-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="rounded-full border border-black/10 bg-white px-3 py-1 text-xs text-[#1E3A8A] hover:border-[#2563EB] hover:bg-[#EFF6FF] transition-colors">
                    {s}
                  </button>
                ))}
              </div>
              <div className="mt-4 font-serif-display italic text-[#1E3A8A]/70">
                Curiosity today. A brighter tomorrow.
              </div>
            </div>

            {/* Hero art */}
            <div className="relative hidden lg:block" data-testid="science-hero-art">
              {art.hero ? (
                <img src={abs(art.hero)} alt="Planet Earth, a microscope, wind turbines and a seedling — science for a brighter tomorrow"
                  className="w-full rounded-3xl object-cover shadow-xl border border-black/5" />
              ) : (
                <div className="aspect-[4/3] w-full rounded-3xl bg-gradient-to-br from-[#DBEAFE] to-[#DCFCE7] border border-black/5 flex items-center justify-center">
                  <Atom size={90} weight="duotone" className="text-[#2563EB]/40" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- 12 PILLAR CIRCLES ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-8" data-testid="science-pillars">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#2563EB]">The 12 pillars</div>
          <GuidePdfDownload url={`${API_BASE}/api/science/guides.pdf`}
            label="Classroom guides (PDF)" testid="science-guides-pdf" color="#1E40AF" />
          <Link to="/passport" data-testid="science-passport-link"
            className="inline-flex items-center gap-2 rounded-full border border-[#B45309]/30 bg-[#FFFBEB] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#B45309] hover:bg-[#FEF3C7] transition-colors">
            My nature passport
          </Link>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-7">
          {PILLAR_DISPLAY.map(({ slug, label }) => {
            const p = pillarBySlug[slug];
            return (
              <div key={slug} className="text-center">
                <Link to={`/science/${slug}`} data-testid={`science-pillar-${slug}`} className="group block">
                  {art[`circle-${slug}`] ? (
                    <img src={abs(art[`circle-${slug}`])} alt={label} loading="lazy"
                      className="mx-auto h-20 w-20 md:h-24 md:w-24 rounded-full object-cover border-2 border-[#2563EB]/10 shadow-sm group-hover:border-[#2563EB]/50 group-hover:scale-105 transition-all" />
                  ) : (
                    <span className="mx-auto flex h-20 w-20 md:h-24 md:w-24 items-center justify-center rounded-full bg-[#EFF6FF] border-2 border-[#2563EB]/10 text-3xl shadow-sm group-hover:border-[#2563EB]/50 group-hover:scale-105 transition-all">
                      {p?.emoji || "🔬"}
                    </span>
                  )}
                  <div className="mt-2 text-[13px] font-medium text-[#0F2A43] group-hover:text-[#2563EB] leading-tight">{label}</div>
                  {p && (
                    <div className="mt-0.5 font-mono-tech text-[8px] tracking-[0.16em] uppercase text-[#6B7280]">
                      {p.subjects.length} subjects · {p.concept_count} concept{p.concept_count === 1 ? "" : "s"}
                    </div>
                  )}
                </Link>
                <Link to={`/read/articles/science-tab-${slug}`} data-testid={`science-pillar-guide-${slug}`}
                  className="mt-0.5 inline-block font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#B85F2A] hover:text-[#a25526]">
                  AI guide →
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---------------- 3 AUDIENCE CARDS ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-14" data-testid="science-audience-cards">
        <div className="grid gap-5 md:grid-cols-3">
          {[
            {
              img: art["card-curious"], title: "For Curious Minds",
              copy: "Simple explanations, amazing facts and fun science for everyone — from kids to lifelong learners.",
              cta: "Start Exploring", to: "#science-pillars", tid: "science-card-curious", fallback: "from-[#DBEAFE] to-[#EFF6FF]",
            },
            {
              img: art["card-impact"], title: "Real Science. Real Impact.",
              copy: "Learn how science helps solve global challenges like climate change, clean energy, health and conservation.",
              cta: "Explore Topics", to: "/science/climate-atmosphere", tid: "science-card-impact", fallback: "from-[#DCFCE7] to-[#F0FDF4]",
            },
            {
              img: art["card-together"], title: "Learn Together",
              copy: "Join a global community of learners, educators and enthusiasts. Ask, share and discover new perspectives.",
              cta: "Join the Community", to: "/community", tid: "science-card-together", fallback: "from-[#FEF3C7] to-[#FFFBEB]",
            },
          ].map((c) => (
            <div key={c.title} className="rounded-3xl border border-black/5 bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={c.tid}>
              {c.img ? (
                <img src={abs(c.img)} alt={c.title} className="h-40 w-full object-cover" loading="lazy" />
              ) : (
                <div className={`h-40 w-full bg-gradient-to-br ${c.fallback}`} />
              )}
              <div className="p-5">
                <div className="font-serif-display text-xl text-[#0F2A43]">{c.title}</div>
                <p className="mt-1.5 text-sm text-[#4B5563] leading-relaxed">{c.copy}</p>
                {c.to.startsWith("#") ? (
                  <a href={c.to} data-testid={`${c.tid}-cta`}
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#DCFCE7] px-5 py-2.5 text-sm font-medium text-[#166534] hover:bg-[#BBF7D0] transition-colors">
                    {c.cta} <ArrowRight size={13} weight="bold" />
                  </a>
                ) : (
                  <Link to={c.to} data-testid={`${c.tid}-cta`}
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#DCFCE7] px-5 py-2.5 text-sm font-medium text-[#166534] hover:bg-[#BBF7D0] transition-colors">
                    {c.cta} <ArrowRight size={13} weight="bold" />
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- SCIENCE TODAY ---------------- */}
      {today.length > 0 && (
        <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-14" data-testid="science-today-strip">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif-display text-2xl md:text-3xl text-[#0F2A43]">Science Today</h2>
            <Link to="/science/today" data-testid="science-today-link" className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#2563EB]">
              News desk <ArrowRight size={11} weight="bold" />
            </Link>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {today.map((n) => (
              <Link key={n.concept_id} to={`/science/concept/${n.concept_id}`} data-testid={`science-today-card-${n.concept_id}`}
                className="rounded-3xl bg-[#E4EAF6] border border-[#1E3A8A]/10 text-[#0F2A43] p-5 hover:-translate-y-0.5 transition-transform">
                <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#1D4ED8]">
                  {n.published_at ? n.published_at.slice(0, 10) : ""}
                </div>
                <div className="mt-1.5 font-serif-display text-lg leading-tight">{n.title}</div>
                <p className="mt-2 text-xs text-[#4B5563] line-clamp-3">{n.quick_answer}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ---------------- LEARNING PATHS ---------------- */}
      {paths.length > 0 && (
        <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-14" data-testid="science-paths">
          <div className="flex items-center gap-2 mb-4">
            <Path size={18} weight="fill" className="text-[#2563EB]" />
            <h2 className="font-serif-display text-2xl md:text-3xl text-[#0F2A43]">Learning paths</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {paths.map((p) => {
              const done = p.steps.filter((s) => s.available).length;
              return (
                <div key={p.slug} data-testid={`science-path-${p.slug}`} className="rounded-3xl bg-[#EDF3F8] border border-[#0F2A43]/10 text-[#0F2A43] p-5">
                  <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#2563EB]">{done}/{p.steps.length} steps live</div>
                  <div className="mt-1 font-serif-display text-xl">{p.label}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {p.steps.map((s) =>
                      s.available ? (
                        <Link key={s.concept_id} to={`/science/concept/${s.concept_id}`}
                          className="rounded-full bg-white border border-black/8 hover:border-[#2563EB]/40 px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.14em] uppercase">
                          {s.title}
                        </Link>
                      ) : (
                        <span key={s.concept_id} className="rounded-full border border-black/10 border-dashed px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.14em] uppercase text-[#9CA3AF]">
                          {s.concept_id.replace(/-/g, " ")}
                        </span>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ---------------- VALUE BAND + QUOTE ---------------- */}
      <section className="mt-14 border-t border-black/5 bg-gradient-to-b from-white to-[#F0FDF4]" data-testid="science-value-band">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-12">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {VALUE_PROPS.map((v) => (
              <div key={v.title} className="flex items-center gap-3">
                <v.icon size={34} weight="duotone" className="text-[#16A34A] shrink-0" />
                <div>
                  <div className="font-semibold text-[#0F2A43]">{v.title}</div>
                  <div className="text-sm text-[#6B7280]">{v.sub}</div>
                </div>
              </div>
            ))}
          </div>
          <blockquote className="mt-10 max-w-2xl mx-auto text-center" data-testid="science-quote">
            <p className="font-serif-display italic text-lg md:text-xl text-[#1E3A8A] leading-relaxed">
              "Science knows no country, because knowledge belongs to humanity, and is the torch which illuminates the world."
            </p>
            <footer className="mt-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#6B7280]">— Louis Pasteur</footer>
          </blockquote>
          <div className="mt-8 flex items-center justify-center gap-2 text-[#16A34A]">
            <HandHeart size={18} weight="fill" />
            <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase">Science connects people and planet</span>
          </div>
        </div>
        <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-8">
          <RelatedReading tag="science-pulse" testid="science-related" />
        </section>
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 pb-6">
          <ScienceNav />
        </div>
      </section>
    </main>
  );
}
